import {useDisclosure} from '@chakra-ui/react';
import React, {useCallback} from 'react';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';
import {ItemCatalogue2StateFF, isItemStateCatalogueFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {
  ArticleCadencierEnleveEventDesc,
  ArticlesCadencierSuggestionsAjoutesEventDesc,
  ItemCatalogue2Desc,
  LigneDePanierDesc,
} from 'api-types/ffconnect-hal';
import {HaliRenderOne, useCommand, useHalState} from '@synako/haligator';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {SectionHeader} from '@/domains/ligneProduit/SectionHeader';
import {Trans, useTranslation} from 'react-i18next';
import {ArticleBlock} from '@/domains/panier/PanierFound/ArticleBlock';
import {DeleteButton} from '@/domains/ligneProduit/DeleteButton';
import {PrixBlockUnitaire} from '../components/PrixBlockUnitaire';
import {useBuyingBlocks} from '../../../hooks/panier/useBuyingBlocks';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {useCadencier, useClient} from '@/haligator-factory';
import {MotifRetraitCadencier, TriSelection} from 'api-types/ffconnect';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {useCapture} from '@/CaptureSetup';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleRetirerDuCadencier} from '../components/ModaleRetirerDuCadencier';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {Box} from '@chakra-ui/layout';

export const CadencierSection: React.FC<{
  title: string;
  id: string;
  articles: TriSelection[number]['articles'];
  lignesDePanier: State<LigneDePanierDesc>[];
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
}> = React.memo(
  ({title, id, articles, lignesDePanier, lastSuggestionEvent}) => {
    const {isOpen, onToggle} = useDisclosure();
    const clientNav = useClient();

    // console.log('RERENDER : CadencierSection '+title);
    if (articles.length === 0) return null;

    return (
      <SectionHeader
        title={title}
        count={articles.length}
        id={id}
        isOpen={isOpen}
        onToggle={onToggle}
      >
        <Box
          marginBottom='1px'
          px='12px'
        >
          {articles.map(({code: code_article}) => {
            return (
              <HaliRenderOne
                key={`ligne_cadencier_${code_article}`}
                nav={clientNav.follow('go:item_catalogue_2', {
                  marketplace_id: 'FF',
                  code: code_article,
                })}
                render={({state: itemState}) => {
                  const ligneDePanier = lignesDePanier.find(
                    (l) => l.data.code_article === code_article,
                  );
                  return (
                    <LigneCadencier
                      key={code_article}
                      ligneDePanier={ligneDePanier}
                      itemState={itemState}
                      lastSuggestionEvent={lastSuggestionEvent}
                    />
                  );
                }}
              />
            );
          })}
        </Box>
      </SectionHeader>
    );
  },
);

const LigneCadencier: React.FC<{
  itemState: State<ItemCatalogue2Desc>;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
}> = React.memo((props) => {
  const {itemState, ...rest} = props;
  if (!isItemStateCatalogueFF(itemState)) return null;
  return (
    <LigneCadencierContent
      {...rest}
      itemState={itemState}
    />
  );
});

const LigneCadencierContent: React.FC<{
  itemState: ItemCatalogue2StateFF;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
}> = React.memo(({itemState, ligneDePanier, lastSuggestionEvent}) => {
  const textToast = useSimpleTextToast();
  const {state: articleState, loading: articleLoading} = useHalState(
    itemState.follow('article'),
  );
  const {t} = useTranslation();
  const code_article = itemState.data.code;
  const capture = useCapture();
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();

  const onSuccess = useCallback(
    (event: State<ArticleCadencierEnleveEventDesc>) => {
      switch (event.data.data.logique) {
        case 'avertissement':
          textToast(
            <Trans t={t}>
              {t('toast.supprime_cadencier_safelive', {code_article})}
            </Trans>,
          );
          break;
        case 'simulation':
          textToast(
            <Trans t={t}>
              {t('toast.supprime_cadencier_simulation', {code_article})}
            </Trans>,
          );
          break;
        case 'normale':
          textToast(
            <Trans t={t}>
              {t('toast.supprime_cadencier', {code_article})}
            </Trans>,
          );
          break;
        default:
          textToast(
            <Trans t={t}>
              {t('toast.supprime_cadencier', {code_article})}
            </Trans>,
          );
          break;
      }
    },
    [textToast, t, code_article],
  );
  const onError = useDefaultOnError(textToast);
  const {
    submit: submitEnleverDuCadencier,
    isDisabled: deleteEnCours,
    isSubmitting,
  } = useCommand(useCadencier(), 'c:enlever-du-cadencier', {
    onSuccess,
    onError,
  });

  const deleteAction = useCallback(() => {
    // Ouvre la modale de confirmation pour retirer du cadencier
    NiceModal.show(ModaleRetirerDuCadencier, {
      validateAction: (raison: MotifRetraitCadencier) => {
        capture({
          name: 'cadencier:enlever',
          props: {depuis: 'cadencier', code: code_article, raison},
        });
        submitEnleverDuCadencier({code_article, motif: raison});
      },
    });
  }, [capture, code_article, submitEnleverDuCadencier]);

  const precommande = itemState.data.tolede.precommande;
  // Hors panier : catégorie catalogue ; la ligne ne sert que si un prix a déjà été saisi.
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    itemState.data.tolede.prix_par_livraison?.[0]?.categorie,
    {contexte: 'catalogue'},
  );
  const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
    textToast,
    'cadencier',
    itemState,
    ligneDePanier,
  );
  const {
    ajouterAvecPrixSaisi,
    isAddingAvecPrix,
    saisieAvantAjout,
    codeArticle,
  } = useAjouterAvecPrixSaisi(itemState, commandeEnColis);

  const prixBlock = (
    <PrixBlockUnitaire
      prixUnitaire={itemState.data.prix_unitaire_ht}
      prixUnitaireTtc={itemState.data.prix_unitaire_ttc}
      uniteFacturation={itemState.data.unite_de_facturation}
      tauxTVA={itemState.data.tva}
      typePrix={itemState.data.tolede.prix_par_livraison![0].type}
      categorieDeTarif={categorieDeTarif}
      modeTarifaireSouhaite={modeTarifaireSouhaite}
      ligneDePanier={ligneDePanier}
      saisieAvantAjout={saisieAvantAjout}
      ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
      codeArticle={codeArticle}
      onEditionPrixChange={onEditionPrixChange}
    />
  );

  const deleteBlock = (
    <DeleteButton
      onClick={deleteAction}
      isLoading={deleteEnCours || isSubmitting}
    />
  );

  if (articleLoading) {
    return 'Chargement...'; // en embedded n'arrive jamais normalement
  }
  if (!articleState) {
    return (
      // eslint-disable-next-line i18next/no-literal-string
      <div>
        Article dans le cadencier mais pas dans la base FF {code_article}
      </div>
    );
  }

  const articleBlock = (
    <ArticleBlock
      code_article={code_article}
      libelles_tronques
      ligneDePanier={ligneDePanier}
      prixDraft={prixDraft}
    />
  );

  return (
    <LigneProduitCommune
      itemState={itemState}
      articleBlock={articleBlock}
      prixBlock={prixBlock}
      uniteQuantiteBlock={uniteQuantiteBlock}
      aPeser={articleState.data.tolede.a_peser}
      buyBlock={buyBlock}
      somethingSubmitting={isSubmitting || isAddingAvecPrix}
      deleteBlock={deleteBlock}
      precommande={precommande}
      ligneDePanier={ligneDePanier}
      lastSuggestionEvent={lastSuggestionEvent}
      afficher_suggestions
    />
  );
});
